import { useState, useEffect, Suspense } from 'react'
import { observer } from 'mobx-react-lite'
// evertel
import { InfoBox, Card, CardHeader, CardBody, Row, Col } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { Button, Text } from '@evertel/web/ui'
import { AuthenticationController } from '@evertel/shared/feature-authentication-auth0'
import { Enroll2FAModal } from './Enroll2FAModal'
import { Unenroll2FAModal } from './Unenroll2FAModal'
import { Confirm2FAEnrollmentModal } from './Confirm2FAEnrollmentModal'
import { Analytics } from '@evertel/analytics'
import { SessionService } from '@evertel/session/SessionService'

const Security = observer(() => {

    const analytics = useService(Analytics)
    const sessionService = useService(SessionService)
    const authenticationController = useService(AuthenticationController)

    const [isLoading, setIsLoading] = useState(false)
    const [showEnrollModal, setShowEnrollModal] = useState(false)
    const [showUnenrollModal, setShowUnenrollModal] = useState(false)
    const [showConfirmEnrollmentModal, setShowConfirmEnrollmentModal] = useState(false)
    const [enrollmentNumber, setEnrollmentNumber] = useState('')

    useEffect(() => {
        initController()
        analytics.logView('/account/security')
    }, [])

    const initController = async () => {
        setIsLoading(true)

        // fetch authenticators
        await authenticationController.getAuthenticators(sessionService.currentUserId)

        setIsLoading(false)
    }

    const toggleEnrollModal = () => {
        setShowEnrollModal(!showEnrollModal)
    }

    const toggleUnenrollModal = () => {
        setShowUnenrollModal(!showUnenrollModal)
    }

    const onCodeRequested = (phoneNumber: string) => {
        setEnrollmentNumber(phoneNumber)
        setShowEnrollModal(false)
        setShowConfirmEnrollmentModal(true)
    }

    const smsAuthenticator = authenticationController.authenticators?.find(a => a.type === 'sms' && a.confirmed === true)
    const emailAuthenticator = authenticationController.authenticators?.find(a => a.type === 'email' && a.confirmed === true)

    // console.log('AUTHENTICATORS', authenticationController.authenticators)

    return (
        <div className="animated fadeIn m-4">
            <Row>
                <Col>
                    <Card>
                        <CardHeader>
                            <h3>Two-Factor Authentication (2FA) Settings</h3>
                        </CardHeader>
                        <CardBody className="">
                            <Row className="mb-5">
                                We'll ask for a security code via the methods below if we
                                notice a login from an unrecognized device or browser.
                            </Row>
                            <Row valign="start" className="flex-wrap">
                                <Col xs={12} lg={6} className="p-4">
                                    <div className="mb-4">
                                        <strong>Text Message (SMS):</strong>
                                        <hr />
                                        <span>We will send a 6-digit security code to your mobile phone as text (SMS)</span>
                                    </div>
                                    <div>
                                        {(!isLoading && !authenticationController.hasSMSAuthenticator) &&
                                            <>
                                                <small>
                                                    To receive security codes via text message, please enroll your mobile number.
                                                </small>
                                                <div className="mt-2">
                                                    <Button
                                                        color="primary"
                                                        onClick={toggleEnrollModal}>
                                                        <Text>Enroll Mobile Number</Text>
                                                    </Button>
                                                </div>
                                            </>
                                        }
                                        {(!isLoading && authenticationController.hasSMSAuthenticator) &&
                                            <>
                                                <div>
                                                    Number enrolled: <span className="text-success">{smsAuthenticator?.name}</span>
                                                </div>
                                                <div className="mt-4">
                                                    To change this number you must first
                                                    <span className="text-primary ms-1 cursor-pointer" onClick={toggleUnenrollModal}>unenroll</span> the current number.
                                                </div>
                                            </>
                                        }
                                    </div>
                                </Col>
                                <Col xs={12} lg={6} className="p-4">
                                    <div className="mb-4">
                                        <strong>Email:</strong>
                                        <hr />
                                        <span>We will send a 6-digit security code to your email address</span>
                                    </div>
                                    <div>
                                        Email enrolled: <span className="text-primary">{emailAuthenticator?.name}</span>

                                        {(!authenticationController.hasSMSAuthenticator) &&
                                            <InfoBox
                                                color="warning"
                                                className="mt-3">
                                                <strong>Please Note:</strong> If your agency has an email firewall (i.e. Mimecast) 2FA emails can be
                                                delayed and can expire before delivery. It is highly recommended in this case, that you enroll in
                                                Text Message (SMS) authentication.
                                            </InfoBox>
                                        }
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>
                </Col>

                <Suspense fallback={'Loading...'}>
                    <Enroll2FAModal
                        isOpen={showEnrollModal && !showConfirmEnrollmentModal}
                        onClose={() => setShowEnrollModal(false)}
                        onCodeRequested={onCodeRequested}
                    />

                    <Unenroll2FAModal
                        isOpen={showUnenrollModal}
                        onClose={() => setShowUnenrollModal(false)}
                        authenticatorId={(smsAuthenticator) ? smsAuthenticator.id : ''}
                    />

                    <Confirm2FAEnrollmentModal
                        isOpen={showConfirmEnrollmentModal}
                        onClose={() => setShowConfirmEnrollmentModal(false)}
                        phoneNumber={enrollmentNumber}
                    />
                </Suspense>
            </Row>
        </div>
    )
})

export {
    Security
}
